import { useEffect, useRef } from 'react';

/**
 * Log-frequency spectrum with peak caps.
 *
 * Linear FFT bins are the wrong shape for music — half the display ends up
 * covering 10–20 kHz, where there's rarely anything, while the kick and snare
 * fight over the first two pixels. Bars are spaced logarithmically instead, so
 * each octave gets equal room and you can actually see a low end.
 *
 * Bars fall slower than they rise, and the caps slower still, because a display
 * that tracks the signal exactly is unreadable at 60fps.
 */
export function Spectrum({
  analyser,
  bars = 32,
  className,
  minHz = 40,
  maxHz = 16000,
  showPeaks = true,
}: {
  analyser: AnalyserNode | null;
  bars?: number;
  className?: string;
  minHz?: number;
  maxHz?: number;
  showPeaks?: boolean;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    const levels = new Float32Array(bars);
    const peaks = new Float32Array(bars);
    let data = new Uint8Array(analyser ? analyser.frequencyBinCount : 512);

    const draw = () => {
      raf = requestAnimationFrame(draw);
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#0b0e11';
      ctx.fillRect(0, 0, w, h);

      if (analyser) {
        if (data.length !== analyser.frequencyBinCount) {
          data = new Uint8Array(analyser.frequencyBinCount);
        }
        analyser.getByteFrequencyData(data as Uint8Array<ArrayBuffer>);
        const nyquist = analyser.context.sampleRate / 2;
        const ratio = Math.pow(maxHz / minHz, 1 / bars);

        for (let b = 0; b < bars; b++) {
          const lo = minHz * Math.pow(ratio, b);
          const hi = minHz * Math.pow(ratio, b + 1);
          const from = Math.floor((lo / nyquist) * data.length);
          const to = Math.max(from + 1, Math.ceil((hi / nyquist) * data.length));
          let sum = 0;
          let n = 0;
          for (let i = from; i < to && i < data.length; i++) {
            sum += data[i];
            n++;
          }
          // Tilt the top end up — real music has far less energy up there, and
          // without it the right half of the display never moves.
          const tilt = 1 + (b / bars) * 0.9;
          const target = Math.min(1, (n ? sum / n / 255 : 0) * tilt);
          levels[b] = target > levels[b] ? target : levels[b] * 0.82 + target * 0.18;
          peaks[b] = Math.max(levels[b], peaks[b] - 0.012);
        }
      } else {
        for (let b = 0; b < bars; b++) {
          levels[b] *= 0.9;
          peaks[b] = Math.max(0, peaks[b] - 0.012);
        }
      }

      const gap = w / bars > 4 ? 1 : 0;
      const bw = w / bars - gap;
      const grad = ctx.createLinearGradient(0, h, 0, 0);
      grad.addColorStop(0, '#6d28d9');
      grad.addColorStop(0.55, '#8b5cf6');
      grad.addColorStop(0.82, '#fbbf24');
      grad.addColorStop(1, '#ff2d95');

      for (let b = 0; b < bars; b++) {
        const x = b * (bw + gap);
        const bh = Math.max(1, levels[b] * h);
        ctx.fillStyle = grad;
        ctx.fillRect(x, h - bh, bw, bh);
        if (showPeaks && peaks[b] > 0.02) {
          ctx.fillStyle = 'rgba(230,235,239,0.55)';
          ctx.fillRect(x, h - Math.max(1.5, peaks[b] * h), bw, 1.5);
        }
      }
    };

    draw();
    return () => cancelAnimationFrame(raf);
  }, [analyser, bars, minHz, maxHz, showPeaks]);

  return <canvas ref={ref} className={className} />;
}
